import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  BookOpen, 
  Target, 
  TrendingUp, 
  Grid3X3, 
  GraduationCap,
  ClipboardList,
  CheckCircle2,
  PlayCircle,
  Users,
  BarChart3,
  Award,
  AlertCircle,
  Lightbulb,
  ArrowRight,
  FileText,
  PenTool,
  FolderOpen,
  Shield,
  Download,
  Eye,
  Link2,
  Archive,
  CheckCircle,
  Clock,
  MapPin,
  Smartphone,
  AlertTriangle,
  Brain
} from 'lucide-react';

const TutorialPage = () => {
  const [activeModule, setActiveModule] = useState('disc');
  const location = useLocation();

  const TAB_MAP = {
    introducao: 'overview',
    recursos: 'purpose',
    videos: 'howto',
    faq: 'tips',
  };

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    if (tabFromUrl && TAB_MAP[tabFromUrl]) return TAB_MAP[tabFromUrl];
    return 'overview';
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    if (tabFromUrl && TAB_MAP[tabFromUrl]) {
      setActiveTab(TAB_MAP[tabFromUrl]);
    }
  }, [location.search]);

  const modules = [
    {
      id: 'disc',
      title: 'Avaliação DISC',
      icon: Target,
      color: 'bg-blue-500',
      description: 'Sistema de avaliação comportamental baseado na metodologia DISC'
    },
    {
      id: 'performance',
      title: 'Avaliação de Desempenho',
      icon: ClipboardList,
      color: 'bg-green-500',
      description: 'Ciclos de avaliação de desempenho e competências'
    },
    {
      id: 'pdi',
      title: 'PDI',
      icon: TrendingUp,
      color: 'bg-purple-500',
      description: 'Plano de Desenvolvimento Individual'
    },
    {
      id: 'ninebox',
      title: '9Box',
      icon: Grid3X3,
      color: 'bg-orange-500',
      description: 'Matriz de performance vs potencial'
    },
    {
      id: 'lms',
      title: 'LMS',
      icon: GraduationCap,
      color: 'bg-indigo-500',
      description: 'Learning Management System'
    },
    {
      id: 'signature',
      title: 'Assinatura Eletrônica',
      icon: PenTool,
      color: 'bg-emerald-500',
      description: 'Sistema de assinatura digital com validade jurídica'
    },
    {
      id: 'contracts',
      title: 'Gestão de Contratos',
      icon: FolderOpen,
      color: 'bg-cyan-500',
      description: 'Gestão completa do ciclo de vida dos contratos'
    },
    {
      id: 'timetracking',
      title: 'Ponto Eletrônico REP-P',
      icon: Clock,
      color: 'bg-slate-500',
      description: 'Sistema de registro eletrônico de ponto conforme Portaria 671/2021'
    },
    {
      id: 'contract-intelligence',
      title: 'Contract Intelligence',
      icon: Brain,
      color: 'bg-indigo-500',
      description: 'Análise inteligente de contratos e documentos com IA'
    },
    {
      id: 'docs',
      title: '📚 Documentação Completa',
      icon: FileText,
      color: 'bg-pink-500',
      description: 'Tutoriais completos, guias e roadmaps de implementação'
    }
  ];

  const discContent = {
    what: "A Avaliação DISC é uma ferramenta de avaliação comportamental baseada na teoria do psicólogo William Moulton Marston. DISC é um acrônimo que representa quatro estilos comportamentais principais:",
    profiles: [
      { letter: 'D', name: 'Dominante', color: 'text-red-600', description: 'Pessoas orientadas para resultados, diretas e decisivas' },
      { letter: 'I', name: 'Influente', color: 'text-yellow-600', description: 'Pessoas sociáveis, otimistas e persuasivas' },
      { letter: 'S', name: 'Estável', color: 'text-green-600', description: 'Pessoas colaborativas, pacientes e leais' },
      { letter: 'C', name: 'Consciente', color: 'text-blue-600', description: 'Pessoas analíticas, precisas e sistemáticas' }
    ],
    purpose: "O DISC ajuda a entender como as pessoas se comportam, se comunicam e tomam decisões, permitindo melhor gestão de equipes, comunicação mais eficaz e desenvolvimento pessoal direcionado.",
    steps: [
      "Acesse o menu 'Avaliação DISC'",
      "Clique em 'Nova Avaliação' para criar uma avaliação",
      "Preencha os dados do participante ou selecione um funcionário existente",
      "O participante responde às 48 perguntas do questionário",
      "O sistema calcula automaticamente o perfil DISC",
      "Visualize o relatório completo com interpretações e recomendações",
      "Para avaliações externas, use 'Links Públicos' para criar links temporários"
    ]
  };

  const performanceContent = {
    what: "O sistema de Avaliação de Desempenho permite criar ciclos estruturados para avaliar o desempenho dos funcionários com base em competências organizacionais e metas específicas.",
    purpose: "Fornece uma metodologia consistente para avaliar, acompanhar e desenvolver o desempenho dos colaboradores, identificando pontos fortes e oportunidades de melhoria.",
    steps: [
      "Acesse 'Avaliação de Desempenho' no menu principal",
      "Na aba 'Ciclos', crie um novo ciclo de avaliação definindo período e objetivos",
      "Configure as competências que serão avaliadas no ciclo",
      "Ative o ciclo para disponibilizar as avaliações",
      "Na aba 'Avaliação', selecione os funcionários e realize as avaliações",
      "Use a escala de 1 a 5 para avaliar cada competência",
      "Adicione comentários e evidências para cada avaliação",
      "Visualize relatórios e análises na aba 'Relatórios'"
    ]
  };

  const pdiContent = {
    what: "O PDI (Plano de Desenvolvimento Individual) é uma ferramenta estratégica que mapeia as necessidades de desenvolvimento de cada colaborador, criando um plano personalizado com ações específicas para crescimento profissional.",
    purpose: "Estrutura o desenvolvimento profissional de forma sistemática, alinhando objetivos individuais com metas organizacionais e criando trilhas de aprendizagem personalizadas.",
    steps: [
      "Acesse o menu 'PDI'",
      "Na aba 'Criar PDI', escolha entre criação manual ou automática",
      "Para criação automática: selecione o funcionário e o ciclo de avaliação",
      "O sistema sugere ações baseadas nos resultados da avaliação de desempenho",
      "Revise e personalize as ações sugeridas",
      "Defina prazos, responsáveis e recursos necessários",
      "Monitore o progresso na aba 'PDIs'",
      "Acompanhe a execução das ações e atualize o status conforme necessário"
    ]
  };

  const nineboxContent = {
    what: "A Matriz 9Box é uma ferramenta de gestão de talentos que posiciona funcionários em uma grade 3x3, cruzando desempenho atual (eixo horizontal) com potencial de crescimento (eixo vertical).",
    purpose: "Facilita decisões estratégicas sobre desenvolvimento de carreira, sucessão, promoções e investimento em talentos, oferecendo uma visão visual e clara do capital humano da empresa.",
    zones: [
      { name: 'Estrelas', position: 'Alto Desempenho + Alto Potencial', action: 'Investir massivamente' },
      { name: 'Especialistas', position: 'Alto Desempenho + Potencial Moderado', action: 'Reconhecer e reter' },
      { name: 'Questionáveis', position: 'Baixo Desempenho + Alto Potencial', action: 'Desenvolver urgentemente' }
    ],
    steps: [
      "Acesse o menu '9Box'",
      "Certifique-se de ter avaliações de desempenho concluídas",
      "Clique em 'Auto Popular' para posicionar funcionários automaticamente",
      "Ou posicione manualmente arrastando os funcionários na matriz",
      "Analise a distribuição dos talentos nas 9 posições",
      "Use a aba 'Analytics' para insights sobre a distribuição",
      "Na aba 'Sucessão', identifique sucessores para posições-chave",
      "Exporte relatórios para reuniões de calibração de talentos"
    ]
  };

  const lmsContent = {
    what: "O LMS (Learning Management System) é uma plataforma completa para gestão de aprendizagem, permitindo criar, distribuir e acompanhar cursos online com diferentes tipos de conteúdo.",
    purpose: "Centraliza o processo de capacitação, oferece trilhas de aprendizagem personalizadas, acompanha o progresso dos colaboradores e integra o desenvolvimento com outras ferramentas de RH.",
    features: [
      'Criação de cursos com vídeos, PDFs, textos e quizzes',
      'Player integrado com suporte a YouTube',
      'Sistema de avaliações e certificados',
      'Trilhas personalizadas baseadas no PDI',
      'Relatórios de progresso e conclusão',
      'Gamificação com badges e rankings'
    ],
    steps: [
      "Acesse o menu 'LMS'",
      "No 'Catálogo', explore os cursos disponíveis",
      "Clique em 'Ver Detalhes' para informações do curso",
      "Matricule-se clicando em 'Matricular-se'",
      "Acesse o curso clicando em 'Acessar Curso'",
      "Navegue pelas aulas usando o menu lateral",
      "Complete cada aula e marque como concluída",
      "Faça os quizzes quando disponíveis",
      "Administradores: use a aba 'Administração' para criar cursos",
      "Atribua cursos específicos a funcionários conforme necessário"
    ]
  };

  const signatureContent = {
    what: "O módulo de Assinatura Eletrônica permite criar, enviar e gerenciar documentos para assinatura digital com validade jurídica completa, seguindo as normas da MP 2.200-2/2001 e legislação brasileira sobre certificação digital.",
    purpose: "Agilizar processos de aprovação, eliminar papel, garantir segurança jurídica nas assinaturas, criar trilha de auditoria completa e reduzir custos operacionais com deslocamentos e impressões.",
    features: [
      'Assinatura com validade jurídica (MP 2.200-2/2001)',
      'Trilha de auditoria completa com IP, data e certificação',
      'Envio por e-mail e WhatsApp',
      'Upload de documentos PDF',
      'Visualização online do documento',
      'Notificações automáticas',
      'Histórico completo de assinaturas',
      'Download do documento assinado'
    ],
    steps: [
      "Acesse 'Jurídico' → 'Assinatura Eletrônica' no menu",
      "Na aba 'Documentos', clique em 'Novo Documento'",
      "Faça upload do arquivo PDF que precisa ser assinado",
      "Preencha o título e descrição do documento",
      "Defina se o documento é obrigatório ou opcional",
      "Adicione tags para facilitar a organização",
      "Clique em 'Salvar Documento'",
      "Na lista de documentos, clique em 'Enviar para Assinatura'",
      "Preencha os dados do signatário (nome, e-mail, CPF)",
      "Escolha o método de envio (E-mail ou WhatsApp)",
      "Clique em 'Enviar Link' para disparar a solicitação",
      "Acompanhe o status na aba 'Assinaturas'",
      "Após assinado, visualize ou baixe o documento na aba 'Documentos Assinados'"
    ]
  };

  const contractsContent = {
    what: "O módulo de Gestão de Contratos é um sistema completo para gerenciar todo o ciclo de vida dos contratos da empresa, desde a criação até o arquivamento, incluindo controle de vencimentos, renovações, aprovações e integração com assinatura eletrônica.",
    purpose: "Centralizar o controle de contratos, automatizar fluxos de aprovação, controlar vencimentos e renovações, integrar com assinatura eletrônica, manter histórico de alterações e garantir compliance legal.",
    features: [
      'Dashboard com visão geral e indicadores',
      'Gestão completa do ciclo de vida dos contratos',
      'Controle de tipos de contratos personalizáveis',
      'Workflow de aprovações configurável',
      'Integração nativa com assinatura eletrônica',
      'Alertas de vencimentos e renovações',
      'Relatórios gerenciais e operacionais',
      'Controle de compliance e auditoria',
      'Upload e gestão de documentos anexos'
    ],
    workflow: [
      { step: 'Criação', description: 'Criação do contrato com dados básicos e documentos' },
      { step: 'Revisão', description: 'Análise técnica e jurídica do contrato' },
      { step: 'Aprovação', description: 'Aprovação pelos responsáveis conforme alçada' },
      { step: 'Assinatura', description: 'Envio para assinatura eletrônica das partes' },
      { step: 'Execução', description: 'Acompanhamento da execução do contrato' },
      { step: 'Renovação', description: 'Processo de renovação ou encerramento' }
    ],
    steps: [
      "Acesse 'Jurídico' → 'Gestão de Contratos' no menu",
      "Visualize o dashboard com estatísticas gerais dos contratos",
      "Na aba 'Contratos', clique em 'Novo Contrato'",
      "Preencha as informações básicas: título, tipo, partes contratantes",
      "Defina valor, moeda, vigência e datas importantes",
      "Selecione prioridade e adicione observações se necessário",
      "Faça upload do documento PDF na seção 'Upload do Documento'",
      "Clique em 'Criar Contrato' para salvar",
      "O contrato aparecerá na lista com status 'Rascunho'",
      "Clique no título do contrato para visualizar o documento anexado",
      "Use 'Editar' para modificar informações ou adicionar documentos",
      "Quando pronto, clique em 'Enviar para Assinatura'",
      "O sistema enviará o documento para o módulo de Assinatura Eletrônica",
      "Acompanhe o progresso nas abas 'Aprovações', 'Assinaturas' e 'Renovações'",
      "Para contratos assinados, use 'Visualizar Documento Assinado'",
      "Gerencie tipos de contratos em 'Cadastros' → 'Tipos de Contratos'"
    ]
  };

  // Conteúdo específico para o módulo de Ponto Eletrônico REP-P
  const timetrackingContent = {
    what: "O Sistema REP-P (Registro Eletrônico de Ponto Portátil) é uma solução digital completa para controle de jornada de trabalho, desenvolvida em total conformidade com a Portaria 671/2021 do Ministério do Trabalho e Previdência e as regulamentações da CLT.",
    features: [
      "Interface Web para funcionários com 4 abas funcionais",
      "Dashboard administrativo com estatísticas em tempo real", 
      "Progressive Web App (PWA) otimizada para mobile",
      "Sistema de geofencing com GPS obrigatório",
      "Validações avançadas conforme CLT (sequência ENTRADA → SAÍDA)",
      "Integridade de dados com SHA-256 em todos os registros",
      "4 tipos de relatórios administrativos (Resumo, Frequência, Horas Extras, Banco de Horas)",
      "Sistema de notificações automáticas em tempo real",
      "Workflow de aprovação para ajustes de ponto",
      "QR Code alternativo para autenticação",
      "Conformidade total com Arquivo Eletrônico de Jornada (AEJ)"
    ],
    purpose: "O Sistema REP-P garante total compliance com a legislação trabalhista brasileira, oferecendo controle rigoroso da jornada de trabalho com integridade, auditabilidade e segurança. Atende às exigências da Portaria 671/2021 para empresas que precisam de registro eletrônico de ponto com validade legal.",
    steps: [
      "Funcionários acessam 'Colaboradores' → 'Controle de Ponto'",
      "Permitem acesso à localização GPS quando solicitado",
      "Verificam se estão dentro da geofence configurada pelo admin",
      "Seguem sequência CLT: ENTRADA → SAÍDA INTERVALO → RETORNO INTERVALO → SAÍDA",
      "Confirmam cada registro e verificam mensagem de sucesso",
      "Consultam histórico na aba 'Histórico' com filtros por data",
      "Geram relatórios pessoais (Espelho de Ponto e AEJ) na aba 'Relatórios'",
      "Administradores acessam 'Colaboradores' → 'Admin - Ponto' para configurações",
      "Configuram geofences e horários de trabalho na aba 'Configurações'",
      "Monitoram estatísticas e aprovam ajustes no dashboard administrativo",
      "Geram relatórios administrativos para análise de frequência e compliance",
      "Para uso mobile, funcionários podem acessar /ponto/mobile e instalar como PWA"
    ],
    workflow: [
      {
        step: "Configuração Inicial",
        description: "Admin configura geofences (áreas permitidas) e horários de trabalho através do dashboard administrativo"
      },
      {
        step: "Registro de Entrada", 
        description: "Funcionário registra ENTRADA dentro da geofence com coordenadas GPS validadas"
      },
      {
        step: "Jornada de Trabalho",
        description: "Durante o dia, registra SAÍDA INTERVALO e RETORNO INTERVALO conforme legislação CLT"
      },
      {
        step: "Registro de Saída",
        description: "Ao final da jornada, registra SAÍDA completando o ciclo diário de trabalho"
      },
      {
        step: "Validações Automáticas",
        description: "Sistema valida horários, intervalos obrigatórios e gera notificações conforme necessário"
      },
      {
        step: "Relatórios e Compliance",
        description: "Gera AEJ e relatórios administrativos para auditoria e conformidade legal"
      }
    ]
  };

  // Conteúdo específico para o Contract Intelligence
  const contractIntelligenceContent = {
    what: "O Contract Intelligence é uma solução revolucionária que utiliza inteligência artificial avançada para análise automatizada de contratos e documentos jurídicos. O sistema combina OCR (reconhecimento óptico de caracteres), processamento de linguagem natural e algoritmos de machine learning para extrair, interpretar e analisar documentos de forma inteligente.",
    features: [
      "OCR Avançado com suporte a PDFs e imagens de alta qualidade",
      "Análise Semântica que compreende contexto e significado dos textos",
      "Detecção Automática de Riscos e cláusulas problemáticas", 
      "Extração Inteligente de dados críticos (prazos, valores, partes)",
      "Comparação Simultânea de múltiplos documentos",
      "Geração de Relatórios executivos com recomendações",
      "Identificação de Datas Críticas e prazos importantes",
      "Análise de Conformidade com regulamentações",
      "Sistema de Classificação de risco (Alto, Médio, Baixo)",
      "Integração com Emergent LLM para análises mais precisas",
      "Armazenamento Seguro e auditável de todos os documentos"
    ],
    purpose: "O Contract Intelligence resolve problemas críticos do setor jurídico: elimina horas de leitura manual, reduz significativamente os erros humanos na interpretação, identifica automaticamente datas críticas evitando prazos perdidos, detecta cláusulas de alto risco que podem passar despercebidas, e centraliza todas as informações contratuais em um local seguro e organizado.",
    steps: [
      "Acesse o menu 'Jurídico' → 'Contract Intelligence'",
      "Na aba 'Upload', clique na área de upload ou arraste arquivos",
      "Selecione contratos em formato PDF, DOC, DOCX, JPG ou PNG (máx. 10MB)",
      "Aguarde o upload ser concluído e status 'Enviado com sucesso'",
      "O sistema iniciará automaticamente a análise com IA",
      "Vá para aba 'Documentos' para ver status da análise",
      "Clique no ícone 🧠 para forçar nova análise se necessário",
      "Acesse a aba 'Resultados' para ver dados extraídos",
      "Revise riscos identificados, dados extraídos e recomendações",
      "Use 'Comparar Documentos' para análise comparativa",
      "Selecione 2+ documentos e clique 'Comparar' para análise conjunta",
      "Exporte resultados em JSON para análises avançadas externas"
    ],
    workflow: [
      {
        step: "Upload de Documento",
        description: "Envio seguro de contratos via interface web com validação de formato e tamanho"
      },
      {
        step: "Extração de Texto (OCR)",
        description: "OCR avançado extrai texto de PDFs nativos e imagens com alta precisão"
      },
      {
        step: "Análise com IA",
        description: "Emergent LLM processa o texto identificando entidades, riscos e dados críticos"
      },
      {
        step: "Classificação de Risco",
        description: "Algoritmos determinam nível de risco baseado em cláusulas identificadas"
      },
      {
        step: "Geração de Relatório",
        description: "Sistema compila análise completa com recomendações e dados extraídos"
      },
      {
        step: "Revisão e Ação",
        description: "Usuário revisa resultados e toma decisões baseadas nas recomendações da IA"
      }
    ]
  };

  const docsContent = {
    what: "Documentação completa e detalhada do sistema GridProfile, incluindo tutoriais passo a passo, guias por perfil de usuário, roadmap de implementação e fluxogramas de processos.",
    documents: [
      {
        title: "Tutorial Completo - Módulos de RH",
        description: "Guia definitivo com 50 seções cobrindo Feedbacks (90º/180º/360º), Avaliação de Desempenho, PDI (10 submódulos) e 9Box. Inclui casos de uso, troubleshooting e FAQ.",
        file: "TUTORIAL_COMPLETO_MODULOS_RH.md",
        icon: BookOpen,
        color: "bg-blue-500",
        size: "~250 KB",
        sections: "50 seções"
      },
      {
        title: "Guia Rápido por Perfil",
        description: "Guias personalizados para cada tipo de usuário: Colaborador (checklists e tutoriais básicos), Gestor (avaliação de equipe, calibração, PDIs), RH (configurações, monitoramento) e Executivo (dashboards estratégicos).",
        file: "GUIA_RAPIDO_POR_PERFIL.md",
        icon: Users,
        color: "bg-green-500",
        size: "~180 KB",
        sections: "4 perfis"
      },
      {
        title: "Roadmap de Implementação",
        description: "Plano estratégico de 12 semanas para implementar o sistema na empresa, incluindo: Preparação (2 sem), Piloto (4 sem), Expansão (4 sem), Consolidação (2 sem). Contém change management, métricas de sucesso e troubleshooting.",
        file: "ROADMAP_IMPLEMENTACAO_EMPRESA.md",
        icon: MapPin,
        color: "bg-purple-500",
        size: "~150 KB",
        sections: "10 capítulos"
      },
      {
        title: "Fluxogramas e Processos",
        description: "Visualização completa dos processos: Ciclo anual de gestão, fluxos detalhados de Feedback 90º/360º, Avaliação de Desempenho, PDI e 9Box. Inclui integrações entre módulos, fluxo de dados/IA e jornadas do Colaborador e Gestor.",
        file: "FLUXOGRAMAS_PROCESSOS.md",
        icon: Grid3X3,
        color: "bg-orange-500",
        size: "~200 KB",
        sections: "10 fluxos"
      }
    ],
    purpose: "Esta documentação completa foi criada para facilitar o onboarding de novos usuários, treinamento de equipes, implementação em empresas e servir como referência técnica. Todo o conteúdo está disponível em formato Markdown para fácil leitura e compartilhamento.",
    steps: [
      "Escolha o documento que melhor atende sua necessidade",
      "Clique no botão 'Ver Documento' para visualizar online",
      "Ou use 'Download' para salvar localmente",
      "Para implementação empresarial, comece pelo Roadmap",
      "Para uso diário, consulte o Guia Rápido por Perfil",
      "Para entendimento profundo, leia o Tutorial Completo",
      "Para visualizar processos, acesse os Fluxogramas"
    ]
  };

  const getContentByModule = (moduleId) => {
    switch (moduleId) {
      case 'disc': return discContent;
      case 'performance': return performanceContent;
      case 'pdi': return pdiContent;
      case 'ninebox': return nineboxContent;
      case 'lms': return lmsContent;
      case 'signature': return signatureContent;
      case 'contracts': return contractsContent;
      case 'timetracking': return timetrackingContent;
      case 'contract-intelligence': return contractIntelligenceContent;
      case 'docs': return docsContent;
      default: return discContent;
    }
  };

  const renderSteps = (steps) => (
    <div className="space-y-3">
      {steps.map((step, index) => (
        <div key={index} className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
            {index + 1}
          </div>
          <p className="text-gray-700">{step}</p>
        </div>
      ))}
    </div>
  );

  const content = getContentByModule(activeModule);
  const currentModule = modules.find(m => m.id === activeModule);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tutorial do Sistema</h1>
          <p className="text-gray-600 mt-2">
            Guia completo para utilizar todas as funcionalidades do sistema de RH
          </p>
        </div>
        <Badge variant="outline" className="px-3 py-1">
          <BookOpen className="h-4 w-4 mr-2" />
          Centro de Ajuda
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Módulos</CardTitle>
              <CardDescription>Selecione um módulo para aprender</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {modules.map((module) => {
                const IconComponent = module.icon;
                return (
                  <Button
                    key={module.id}
                    variant={activeModule === module.id ? "default" : "ghost"}
                    className="w-full justify-start"
                    onClick={() => setActiveModule(module.id)}
                  >
                    <IconComponent className="h-4 w-4 mr-3" />
                    <span className="text-left">{module.title}</span>
                  </Button>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${currentModule?.color} text-white`}>
                  {React.createElement(currentModule?.icon, { className: "h-6 w-6" })}
                </div>
                <div>
                  <CardTitle className="text-2xl">{currentModule?.title}</CardTitle>
                  <CardDescription>{currentModule?.description}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="overview">Visão Geral</TabsTrigger>
                  <TabsTrigger value="purpose">Finalidade</TabsTrigger>
                  <TabsTrigger value="howto">Como Usar</TabsTrigger>
                  <TabsTrigger value="tips">Dicas</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center">
                      <AlertCircle className="h-5 w-5 mr-2 text-blue-500" />
                      O que é?
                    </h3>
                    <p className="text-gray-700 leading-relaxed">{content.what}</p>
                  </div>

                  {/* DISC Profiles */}
                  {activeModule === 'disc' && (
                    <div>
                      <h4 className="font-semibold mb-3">Perfis DISC:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {content.profiles.map((profile) => (
                          <div key={profile.letter} className="border rounded-lg p-4">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className={`w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center font-bold ${profile.color}`}>
                                {profile.letter}
                              </div>
                              <span className="font-semibold">{profile.name}</span>
                            </div>
                            <p className="text-sm text-gray-600">{profile.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 9Box Zones */}
                  {activeModule === 'ninebox' && (
                    <div>
                      <h4 className="font-semibold mb-3">Principais Zonas da Matriz:</h4>
                      <div className="space-y-3">
                        {content.zones.map((zone, index) => (
                          <div key={index} className="border-l-4 border-orange-500 pl-4 py-2">
                            <h5 className="font-medium">{zone.name}</h5>
                            <p className="text-sm text-gray-600">{zone.position}</p>
                            <p className="text-sm text-orange-600 font-medium">{zone.action}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* LMS Features */}
                  {activeModule === 'lms' && (
                    <div>
                      <h4 className="font-semibold mb-3">Principais Funcionalidades:</h4>
                      <ul className="space-y-2">
                        {content.features.map((feature, index) => (
                          <li key={index} className="flex items-center space-x-2">
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Signature Features */}
                  {activeModule === 'signature' && (
                    <div>
                      <h4 className="font-semibold mb-3">Principais Funcionalidades:</h4>
                      <ul className="space-y-2">
                        {content.features.map((feature, index) => (
                          <li key={index} className="flex items-center space-x-2">
                            <Shield className="h-4 w-4 text-emerald-500" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Contracts Features */}
                  {activeModule === 'contracts' && (
                    <>
                      <div>
                        <h4 className="font-semibold mb-3">Principais Funcionalidades:</h4>
                        <ul className="space-y-2">
                          {content.features.map((feature, index) => (
                            <li key={index} className="flex items-center space-x-2">
                              <CheckCircle2 className="h-4 w-4 text-cyan-500" />
                              <span className="text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold mb-3">Workflow do Contrato:</h4>
                        <div className="space-y-3">
                          {content.workflow.map((item, index) => (
                            <div key={index} className="flex items-start space-x-3">
                              <div className="flex-shrink-0 w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                                {index + 1}
                              </div>
                              <div>
                                <h5 className="font-medium text-cyan-800">{item.step}</h5>
                                <p className="text-sm text-gray-600">{item.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </TabsContent>

                <TabsContent value="purpose" className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center">
                      <Target className="h-5 w-5 mr-2 text-green-500" />
                      Para que serve?
                    </h3>
                    <p className="text-gray-700 leading-relaxed">{content.purpose}</p>
                  </div>
                </TabsContent>

                <TabsContent value="howto" className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                      <PlayCircle className="h-5 w-5 mr-2 text-purple-500" />
                      Passo a Passo
                    </h3>
                    {renderSteps(content.steps)}
                  </div>
                </TabsContent>

                <TabsContent value="tips" className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                      <Lightbulb className="h-5 w-5 mr-2 text-yellow-500" />
                      Dicas e Melhores Práticas
                    </h3>
                    
                    {activeModule === 'disc' && (
                      <div className="space-y-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-medium text-blue-800 mb-2">📊 Interpretação de Resultados</h4>
                          <ul className="text-blue-700 text-sm space-y-1">
                            <li>• Foque no perfil primário, mas considere o secundário</li>
                            <li>• Use os resultados para adaptar sua comunicação</li>
                            <li>• Combine DISC com outras avaliações para visão completa</li>
                          </ul>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h4 className="font-medium text-green-800 mb-2">🎯 Aplicação Prática</h4>
                          <ul className="text-green-700 text-sm space-y-1">
                            <li>• Use para formação de equipes complementares</li>
                            <li>• Aplique em processos de seleção e desenvolvimento</li>
                            <li>• Crie estratégias de motivação personalizadas</li>
                          </ul>
                        </div>
                      </div>
                    )}

                    {activeModule === 'performance' && (
                      <div className="space-y-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h4 className="font-medium text-green-800 mb-2">📈 Condução de Avaliações</h4>
                          <ul className="text-green-700 text-sm space-y-1">
                            <li>• Mantenha objetividade e imparcialidade</li>
                            <li>• Use exemplos concretos nas avaliações</li>
                            <li>• Combine auto-avaliação com avaliação de superiores</li>
                          </ul>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <h4 className="font-medium text-yellow-800 mb-2">⚖️ Calibração</h4>
                          <ul className="text-yellow-700 text-sm space-y-1">
                            <li>• Realize reuniões de calibração entre avaliadores</li>
                            <li>• Compare avaliações de funcionários similares</li>
                            <li>• Documente justificativas para notas extremas</li>
                          </ul>
                        </div>
                      </div>
                    )}

                    {activeModule === 'pdi' && (
                      <div className="space-y-4">
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <h4 className="font-medium text-purple-800 mb-2">🎯 Definição de Metas</h4>
                          <ul className="text-purple-700 text-sm space-y-1">
                            <li>• Use critérios SMART (Específico, Mensurável, Atingível, Relevante, Temporal)</li>
                            <li>• Alinhe objetivos individuais com metas organizacionais</li>
                            <li>• Defina indicadores claros de sucesso</li>
                          </ul>
                        </div>
                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                          <h4 className="font-medium text-indigo-800 mb-2">🔄 Acompanhamento</h4>
                          <ul className="text-indigo-700 text-sm space-y-1">
                            <li>• Realize reuniões de follow-up regulares</li>
                            <li>• Ajuste ações conforme necessário</li>
                            <li>• Celebre conquistas e marcos alcançados</li>
                          </ul>
                        </div>
                      </div>
                    )}

                    {activeModule === 'ninebox' && (
                      <div className="space-y-4">
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                          <h4 className="font-medium text-orange-800 mb-2">📊 Posicionamento</h4>
                          <ul className="text-orange-700 text-sm space-y-1">
                            <li>• Base-se em dados objetivos de performance</li>
                            <li>• Considere potencial através de competências comportamentais</li>
                            <li>• Revise posicionamentos periodicamente</li>
                          </ul>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <h4 className="font-medium text-red-800 mb-2">⚠️ Cuidados</h4>
                          <ul className="text-red-700 text-sm space-y-1">
                            <li>• Evite rótulos permanentes - pessoas podem evoluir</li>
                            <li>• Use como ferramenta de desenvolvimento, não punição</li>
                            <li>• Mantenha confidencialidade dos resultados</li>
                          </ul>
                        </div>
                      </div>
                    )}

                    {activeModule === 'lms' && (
                      <div className="space-y-4">
                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                          <h4 className="font-medium text-indigo-800 mb-2">🎓 Criação de Conteúdo</h4>
                          <ul className="text-indigo-700 text-sm space-y-1">
                            <li>• Varie tipos de conteúdo: vídeos, textos, quizzes</li>
                            <li>• Mantenha aulas curtas e focadas (15-30 min)</li>
                            <li>• Use títulos descritivos e objetivos claros</li>
                          </ul>
                        </div>
                        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                          <h4 className="font-medium text-teal-800 mb-2">📈 Engajamento</h4>
                          <ul className="text-teal-700 text-sm space-y-1">
                            <li>• Integre com PDI para trilhas personalizadas</li>
                            <li>• Use gamificação para motivar conclusão</li>
                            <li>• Monitore progresso e ofereça suporte</li>
                          </ul>
                        </div>
                      </div>
                    )}

                    {activeModule === 'signature' && (
                      <div className="space-y-4">
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                          <h4 className="font-medium text-emerald-800 mb-2">🔒 Segurança Jurídica</h4>
                          <ul className="text-emerald-700 text-sm space-y-1">
                            <li>• Sempre use CPF correto do signatário para validade legal</li>
                            <li>• Verifique se o documento está completo antes de enviar</li>
                            <li>• Mantenha backup dos documentos assinados</li>
                            <li>• Use títulos descritivos para facilitar identificação</li>
                          </ul>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-medium text-blue-800 mb-2">📧 Envio Eficiente</h4>
                          <ul className="text-blue-700 text-sm space-y-1">
                            <li>• Prefira WhatsApp para comunicação mais rápida</li>
                            <li>• Use e-mail para documentos mais formais</li>
                            <li>• Acompanhe regularmente o status das assinaturas</li>
                            <li>• Configure lembretes automáticos quando disponível</li>
                          </ul>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <h4 className="font-medium text-yellow-800 mb-2">⚖️ Aspectos Legais</h4>
                          <ul className="text-yellow-700 text-sm space-y-1">
                            <li>• Todas as assinaturas seguem MP 2.200-2/2001</li>
                            <li>• IP, data e certificação são registrados automaticamente</li>
                            <li>• Documentos têm validade jurídica plena</li>
                            <li>• Mantenha trilha de auditoria para compliance</li>
                          </ul>
                        </div>
                      </div>
                    )}

                    {activeModule === 'contracts' && (
                      <div className="space-y-4">
                        <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                          <h4 className="font-medium text-cyan-800 mb-2">📋 Organização</h4>
                          <ul className="text-cyan-700 text-sm space-y-1">
                            <li>• Use tipos de contratos para categorizar adequadamente</li>
                            <li>• Defina valores e moedas corretamente</li>
                            <li>• Configure alertas de vencimento com antecedência</li>
                            <li>• Mantenha observações atualizadas</li>
                          </ul>
                        </div>
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <h4 className="font-medium text-purple-800 mb-2">🔄 Workflow</h4>
                          <ul className="text-purple-700 text-sm space-y-1">
                            <li>• Revise todos os dados antes de enviar para assinatura</li>
                            <li>• Use prioridades para organizar fila de trabalho</li>
                            <li>• Integre com assinatura eletrônica para agilizar processo</li>
                            <li>• Monitore prazos e renovações regularmente</li>
                          </ul>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <h4 className="font-medium text-red-800 mb-2">⚠️ Compliance</h4>
                          <ul className="text-red-700 text-sm space-y-1">
                            <li>• Mantenha documentos originais arquivados</li>
                            <li>• Documente todas as alterações e aprovações</li>
                            <li>• Use relatórios para auditorias internas</li>
                            <li>• Monitore vencimentos para evitar renovações automáticas indesejadas</li>
                          </ul>
                        </div>
                      </div>
                    )}

                    {activeModule === 'contract-intelligence' && (
                      <div className="space-y-4">
                        {/* O que é o Contract Intelligence */}
                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Brain className="h-5 w-5 text-indigo-600" />
                            <h4 className="font-medium text-indigo-800">O que é o Contract Intelligence?</h4>
                          </div>
                          <p className="text-indigo-700 text-sm mb-3">
                            O Contract Intelligence é uma solução revolucionária que utiliza <strong>inteligência artificial avançada</strong> 
                            para análise automatizada de contratos e documentos jurídicos. Combina OCR, processamento de linguagem natural 
                            e algoritmos de machine learning para extrair, interpretar e analisar documentos de forma inteligente.
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                            <div className="bg-white border border-indigo-100 rounded p-3 text-center">
                              <div className="text-2xl mb-2">🔍</div>
                              <div className="text-xs font-medium text-indigo-800">OCR Avançado</div>
                              <div className="text-xs text-indigo-600">PDFs e Imagens</div>
                            </div>
                            <div className="bg-white border border-indigo-100 rounded p-3 text-center">
                              <div className="text-2xl mb-2">🧠</div>
                              <div className="text-xs font-medium text-indigo-800">IA Semântica</div>
                              <div className="text-xs text-indigo-600">Contexto e Significado</div>
                            </div>
                            <div className="bg-white border border-indigo-100 rounded p-3 text-center">
                              <div className="text-2xl mb-2">⚠️</div>
                              <div className="text-xs font-medium text-indigo-800">Detecção Riscos</div>
                              <div className="text-xs text-indigo-600">Cláusulas Perigosas</div>
                            </div>
                          </div>
                        </div>

                        {/* Capacidades da IA */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Target className="h-5 w-5 text-green-600" />
                            <h4 className="font-medium text-green-800">Capacidades da IA</h4>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="bg-white border border-green-100 rounded p-3">
                              <h5 className="font-medium text-green-800 text-sm mb-2">🔤 Processamento de Texto</h5>
                              <ul className="text-green-700 text-xs space-y-1">
                                <li>• <strong>OCR Avançado</strong>: Extrai texto de PDFs e imagens</li>
                                <li>• <strong>NLP</strong>: Processamento de linguagem natural</li>
                                <li>• <strong>Análise Semântica</strong>: Compreende contexto</li>
                                <li>• <strong>Reconhecimento</strong>: Entidades e padrões</li>
                              </ul>
                            </div>
                            <div className="bg-white border border-green-100 rounded p-3">
                              <h5 className="font-medium text-green-800 text-sm mb-2">🎯 Análise Inteligente</h5>
                              <ul className="text-green-700 text-xs space-y-1">
                                <li>• <strong>Detecção de Riscos</strong>: Cláusulas problemáticas</li>
                                <li>• <strong>Extração de Dados</strong>: Prazos, valores, partes</li>
                                <li>• <strong>Comparação</strong>: Múltiplos documentos</li>
                                <li>• <strong>Recomendações</strong>: Sugestões inteligentes</li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        {/* Problemas que Resolve */}
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <CheckCircle className="h-5 w-5 text-red-600" />
                            <h4 className="font-medium text-red-800">Problemas que Resolve</h4>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-red-600" />
                                <span className="text-sm font-medium text-red-800">Tempo Perdido</span>
                              </div>
                              <p className="text-xs text-red-700 ml-6">Elimina horas de leitura manual de contratos</p>
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-red-600" />
                                <span className="text-sm font-medium text-red-800">Erros Humanos</span>
                              </div>
                              <p className="text-xs text-red-700 ml-6">Reduz falhas na interpretação de cláusulas</p>
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-red-600" />
                                <span className="text-sm font-medium text-red-800">Prazos Perdidos</span>
                              </div>
                              <p className="text-xs text-red-700 ml-6">Identifica datas críticas automaticamente</p>
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-red-600" />
                                <span className="text-sm font-medium text-red-800">Riscos Ocultos</span>
                              </div>
                              <p className="text-xs text-red-700 ml-6">Detecta cláusulas perigosas que passam despercebidas</p>
                            </div>
                          </div>
                        </div>

                        {/* Tipos de Documentos */}
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <FileText className="h-5 w-5 text-purple-600" />
                            <h4 className="font-medium text-purple-800">Tipos de Documentos Suportados</h4>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h5 className="font-medium text-purple-800 text-sm mb-2">📄 Contratos e Acordos</h5>
                              <ul className="text-purple-700 text-xs space-y-1">
                                <li>• Contratos comerciais e de prestação de serviços</li>
                                <li>• NDAs e acordos de confidencialidade</li>
                                <li>• Contratos de trabalho e terceirização</li>
                                <li>• Acordos de parceria e joint venture</li>
                              </ul>
                            </div>
                            <div>
                              <h5 className="font-medium text-purple-800 text-sm mb-2">⚖️ Documentos Jurídicos</h5>
                              <ul className="text-purple-700 text-xs space-y-1">
                                <li>• Propostas e orçamentos detalhados</li>
                                <li>• Termos de uso e políticas de privacidade</li>
                                <li>• Documentos regulatórios</li>
                                <li>• Atas e deliberações</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Documentação Completa */}
                    {activeModule === 'docs' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {content.documents.map((doc, index) => {
                            const IconComponent = doc.icon;
                            return (
                              <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
                                <div className="flex items-start space-x-3 mb-3">
                                  <div className={`p-2 rounded-lg ${doc.color} text-white`}>
                                    <IconComponent className="h-5 w-5" />
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="font-semibold text-gray-900 mb-1">{doc.title}</h4>
                                    <div className="flex items-center space-x-2 text-xs text-gray-500 mb-2">
                                      <span>{doc.size}</span>
                                      <span>•</span>
                                      <span>{doc.sections}</span>
                                    </div>
                                  </div>
                                </div>
                                <p className="text-sm text-gray-600 mb-3">{doc.description}</p>
                                <div className="flex space-x-2">
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="flex-1"
                                    onClick={() => window.open(`/docs/${doc.file}`, '_blank')}
                                  >
                                    <Eye className="h-4 w-4 mr-1" />
                                    Ver Documento
                                  </Button>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = `/docs/${doc.file}`;
                                      link.download = doc.file;
                                      link.click();
                                    }}
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {activeModule === 'timetracking' && (
                      <div className="space-y-4">
                        {/* O que é o Sistema REP-P */}
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Clock className="h-5 w-5 text-slate-600" />
                            <h4 className="font-medium text-slate-800">O que é o Sistema REP-P?</h4>
                          </div>
                          <p className="text-slate-700 text-sm mb-3">
                            O Sistema de Ponto Eletrônico REP-P (Registro Eletrônico de Ponto Portátil) é uma solução digital completa 
                            para controle de jornada de trabalho, desenvolvida em total conformidade com a <strong>Portaria 671/2021</strong> 
                            do Ministério do Trabalho e Previdência e as regulamentações da <strong>CLT</strong>.
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                            <div className="bg-white border border-slate-100 rounded p-3 text-center">
                              <Smartphone className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                              <div className="text-xs font-medium text-slate-800">3 Interfaces</div>
                              <div className="text-xs text-slate-600">Web, Admin, PWA</div>
                            </div>
                            <div className="bg-white border border-slate-100 rounded p-3 text-center">
                              <MapPin className="h-6 w-6 text-green-600 mx-auto mb-2" />
                              <div className="text-xs font-medium text-slate-800">Geofencing</div>
                              <div className="text-xs text-slate-600">GPS obrigatório</div>
                            </div>
                            <div className="bg-white border border-slate-100 rounded p-3 text-center">
                              <Shield className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                              <div className="text-xs font-medium text-slate-800">SHA-256</div>
                              <div className="text-xs text-slate-600">Integridade total</div>
                            </div>
                          </div>
                        </div>

                        {/* Leis e Regulamentações */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <FileText className="h-5 w-5 text-blue-600" />
                            <h4 className="font-medium text-blue-800">Leis e Regulamentações</h4>
                          </div>
                          <div className="space-y-3">
                            <div className="bg-white border border-blue-100 rounded p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline" className="text-xs">Portaria 671/2021</Badge>
                                <Badge variant="outline" className="text-xs bg-green-50 text-green-700">REP-P Tipo</Badge>
                              </div>
                              <ul className="text-blue-700 text-sm space-y-1">
                                <li>• <strong>Art. 3º</strong> - Identificação da empresa e estabelecimento</li>
                                <li>• <strong>Art. 4º</strong> - Identificação do empregado</li>
                                <li>• <strong>Art. 5º</strong> - Data e horário da marcação com precisão</li>
                                <li>• <strong>Art. 6º</strong> - Localização da marcação (coordenadas GPS)</li>
                                <li>• <strong>Art. 8º</strong> - Integridade e inalterabilidade dos dados</li>
                                <li>• <strong>Art. 12º</strong> - Armazenamento seguro por 5 anos</li>
                              </ul>
                            </div>
                            <div className="bg-white border border-blue-100 rounded p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline" className="text-xs">CLT</Badge>
                                <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700">Artigos</Badge>
                              </div>
                              <ul className="text-blue-700 text-sm space-y-1">
                                <li>• <strong>Art. 58</strong> - Duração normal do trabalho (8 horas diárias)</li>
                                <li>• <strong>Art. 71</strong> - Intervalos obrigatórios (1h após 6h trabalhadas)</li>
                                <li>• <strong>Art. 74</strong> - Controle de frequência obrigatório</li>
                                <li>• <strong>Art. 59</strong> - Regime de compensação e banco de horas</li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        {/* Segurança Implementada */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Shield className="h-5 w-5 text-green-600" />
                            <h4 className="font-medium text-green-800">Segurança Implementada</h4>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="bg-white border border-green-100 rounded p-3">
                              <h5 className="font-medium text-green-800 text-sm mb-2">🔐 Criptografia e Integridade</h5>
                              <ul className="text-green-700 text-xs space-y-1">
                                <li>• <strong>SHA-256</strong>: Hash de integridade em cada registro</li>
                                <li>• <strong>JWT</strong>: Autenticação segura com tokens</li>
                                <li>• <strong>Timestamping</strong>: Carimbo temporal preciso (UTC)</li>
                                <li>• <strong>Imutabilidade</strong>: Registros não podem ser alterados</li>
                              </ul>
                            </div>
                            <div className="bg-white border border-green-100 rounded p-3">
                              <h5 className="font-medium text-green-800 text-sm mb-2">📍 Controle de Localização</h5>
                              <ul className="text-green-700 text-xs space-y-1">
                                <li>• <strong>GPS Obrigatório</strong>: Coordenadas em todos os registros</li>
                                <li>• <strong>Geofencing</strong>: Áreas permitidas configuráveis</li>
                                <li>• <strong>Validação em Tempo Real</strong>: Bloqueio automático fora da área</li>
                                <li>• <strong>Precisão</strong>: Coordenadas com 6 casas decimais</li>
                              </ul>
                            </div>
                            <div className="bg-white border border-green-100 rounded p-3">
                              <h5 className="font-medium text-green-800 text-sm mb-2">🛡️ Validações Avançadas</h5>
                              <ul className="text-green-700 text-xs space-y-1">
                                <li>• <strong>Sequência CLT</strong>: ENTRADA → SAÍDA → INTERVALOS</li>
                                <li>• <strong>Horário Comercial</strong>: Validação 07:00 - 20:00</li>
                                <li>• <strong>Intervalos Obrigatórios</strong>: Após 6h trabalhadas</li>
                                <li>• <strong>Duplicação</strong>: Prevenção de registros duplicados</li>
                              </ul>
                            </div>
                            <div className="bg-white border border-green-100 rounded p-3">
                              <h5 className="font-medium text-green-800 text-sm mb-2">📊 Auditoria e Compliance</h5>
                              <ul className="text-green-700 text-xs space-y-1">
                                <li>• <strong>AEJ</strong>: Arquivo Eletrônico de Jornada automático</li>
                                <li>• <strong>Logs Detalhados</strong>: Rastreamento completo de operações</li>
                                <li>• <strong>Backup Seguro</strong>: Armazenamento MongoDB criptografado</li>
                                <li>• <strong>Relatórios</strong>: 4 tipos de relatórios administrativos</li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        {/* Funcionalidades Principais */}
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <BarChart3 className="h-5 w-5 text-purple-600" />
                            <h4 className="font-medium text-purple-800">Funcionalidades Principais</h4>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="bg-white border border-purple-100 rounded p-3">
                              <h5 className="font-medium text-purple-800 text-sm mb-2">👤 Interface Funcionário</h5>
                              <ul className="text-purple-700 text-xs space-y-1">
                                <li>• Marcar Ponto (4 tipos de registro)</li>
                                <li>• Histórico com filtros por data</li>
                                <li>• Relatórios (Espelho e AEJ)</li>
                                <li>• Configurações pessoais</li>
                                <li>• Banco de horas individual</li>
                                <li>• Status em tempo real</li>
                              </ul>
                            </div>
                            <div className="bg-white border border-purple-100 rounded p-3">
                              <h5 className="font-medium text-purple-800 text-sm mb-2">⚙️ Dashboard Admin</h5>
                              <ul className="text-purple-700 text-xs space-y-1">
                                <li>• Estatísticas em tempo real</li>
                                <li>• Aprovação de ajustes</li>
                                <li>• Gestão de funcionários</li>
                                <li>• Configuração de geofences</li>
                                <li>• Horários de trabalho</li>
                                <li>• Relatórios administrativos</li>
                              </ul>
                            </div>
                            <div className="bg-white border border-purple-100 rounded p-3">
                              <h5 className="font-medium text-purple-800 text-sm mb-2">📱 PWA Mobile</h5>
                              <ul className="text-purple-700 text-xs space-y-1">
                                <li>• Design mobile-first</li>
                                <li>• Funciona offline</li>
                                <li>• QR Code alternativo</li>
                                <li>• Feedback táctil (vibração)</li>
                                <li>• Instalação como app</li>
                                <li>• Interface otimizada</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Tab Passo a Passo - CONTEÚDO ESPECÍFICO PARA REP-P */}
                <TabsContent value="steps" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <PlayCircle className="h-5 w-5 text-blue-600" />
                        Passo a Passo - {modules.find(m => m.id === activeModule)?.title}
                      </CardTitle>
                      <CardDescription>
                        {activeModule === 'timetracking' 
                          ? 'Guias detalhados para administradores e funcionários'
                          : activeModule === 'contract-intelligence'
                          ? 'Guia passo a passo para análise inteligente de contratos' 
                          : 'Instruções detalhadas para utilização do módulo'
                        }
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {activeModule === 'contract-intelligence' && (
                        <div className="space-y-6">
                          {/* Guia para Usuários */}
                          <div className="border border-indigo-200 rounded-lg p-4 bg-indigo-50">
                            <div className="flex items-center gap-2 mb-4">
                              <Brain className="h-5 w-5 text-indigo-600" />
                              <h3 className="font-semibold text-indigo-800">Como Usar o Contract Intelligence</h3>
                            </div>
                            
                            <div className="space-y-4">
                              <div>
                                <h4 className="font-medium text-indigo-800 mb-2">1️⃣ Acesso e Upload</h4>
                                <div className="bg-white border border-indigo-100 rounded p-3 text-sm">
                                  <ol className="text-indigo-700 space-y-2 list-decimal list-inside">
                                    <li>Acesse o menu <strong>Jurídico → Contract Intelligence</strong></li>
                                    <li>Na aba <strong>"Upload"</strong>, visualize a área de envio</li>
                                    <li>Clique na área ou arraste arquivos diretamente:
                                      <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                                        <li>Formatos aceitos: PDF, DOC, DOCX, JPG, PNG</li>
                                        <li>Tamanho máximo: 10MB por arquivo</li>
                                        <li>Múltiplos arquivos podem ser enviados simultaneamente</li>
                                      </ul>
                                    </li>
                                    <li>Aguarde confirmação de <strong>"Enviado com sucesso"</strong></li>
                                  </ol>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-medium text-indigo-800 mb-2">2️⃣ Análise Automática</h4>
                                <div className="bg-white border border-indigo-100 rounded p-3 text-sm">
                                  <ol className="text-indigo-700 space-y-2 list-decimal list-inside">
                                    <li>O sistema inicia automaticamente a <strong>análise com IA</strong></li>
                                    <li>Vá para aba <strong>"Documentos"</strong> para acompanhar o progresso</li>
                                    <li>Status disponíveis:
                                      <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                                        <li><strong>Pendente</strong>: Aguardando processamento</li>
                                        <li><strong>Processando</strong>: IA analisando o documento</li>
                                        <li><strong>Analisado</strong>: Análise concluída com sucesso</li>
                                        <li><strong>Erro</strong>: Falha no processamento</li>
                                      </ul>
                                    </li>
                                    <li>Forçar nova análise clicando no ícone 🧠</li>
                                  </ol>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-medium text-indigo-800 mb-2">3️⃣ Revisão de Resultados</h4>
                                <div className="bg-white border border-indigo-100 rounded p-3 text-sm">
                                  <ol className="text-indigo-700 space-y-2 list-decimal list-inside">
                                    <li>Acesse a aba <strong>"Resultados"</strong> para ver a análise</li>
                                    <li>Revise as estatísticas principais:
                                      <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                                        <li><strong>Nível de Confiança</strong>: Precisão da análise (0-100%)</li>
                                        <li><strong>Entidades Extraídas</strong>: Quantidade de dados encontrados</li>
                                        <li><strong>Riscos Identificados</strong>: Número de cláusulas problemáticas</li>
                                      </ul>
                                    </li>
                                    <li>Analise <strong>dados extraídos</strong>: partes, datas, valores</li>
                                    <li>Revise <strong>riscos identificados</strong> e suas recomendações</li>
                                    <li>Leia o <strong>resumo executivo</strong> gerado pela IA</li>
                                  </ol>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-medium text-indigo-800 mb-2">4️⃣ Comparação de Documentos</h4>
                                <div className="bg-white border border-indigo-100 rounded p-3 text-sm">
                                  <ol className="text-indigo-700 space-y-2 list-decimal list-inside">
                                    <li>Na aba <strong>"Documentos"</strong>, clique em <strong>"Comparar Documentos"</strong></li>
                                    <li>Selecione <strong>2 ou mais documentos</strong> usando checkboxes</li>
                                    <li>Clique em <strong>"Comparar (X)"</strong> para iniciar análise comparativa</li>
                                    <li>Aguarde processamento da comparação pela IA</li>
                                    <li>Na aba <strong>"Resultados"</strong>, revise:
                                      <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                                        <li><strong>Semelhanças</strong>: Cláusulas e termos em comum</li>
                                        <li><strong>Diferenças</strong>: Variações significativas</li>
                                        <li><strong>Comparação de Riscos</strong>: Níveis entre documentos</li>
                                        <li><strong>Recomendações</strong>: Sugestões da IA</li>
                                      </ul>
                                    </li>
                                  </ol>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-medium text-indigo-800 mb-2">5️⃣ Exportação e Arquivamento</h4>
                                <div className="bg-white border border-indigo-100 rounded p-3 text-sm">
                                  <ol className="text-indigo-700 space-y-2 list-decimal list-inside">
                                    <li>Na aba <strong>"Resultados"</strong>, use <strong>"Exportar JSON"</strong></li>
                                    <li>Salve resultados para análises externas avançadas</li>
                                    <li>Documente ações tomadas baseadas nas recomendações</li>
                                    <li>Mantenha histórico de análises na aba <strong>"Documentos"</strong></li>
                                    <li>Use filtros de data para organizar documentos antigos</li>
                                  </ol>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Fluxo de Trabalho Recomendado */}
                          <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                            <div className="flex items-center gap-2 mb-4">
                              <Target className="h-5 w-5 text-green-600" />
                              <h3 className="font-semibold text-green-800">Fluxo de Trabalho Recomendado</h3>
                            </div>

                            <div className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-3">
                                  <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-bold">1</div>
                                    <div>
                                      <h4 className="font-medium text-green-800">Preparação</h4>
                                      <p className="text-sm text-green-700">Organize documentos por categoria ou projeto</p>
                                    </div>
                                  </div>
                                  <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-bold">2</div>
                                    <div>
                                      <h4 className="font-medium text-green-800">Upload em Lotes</h4>
                                      <p className="text-sm text-green-700">Envie múltiplos documentos relacionados juntos</p>
                                    </div>
                                  </div>
                                  <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-bold">3</div>
                                    <div>
                                      <h4 className="font-medium text-green-800">Análise Individual</h4>
                                      <p className="text-sm text-green-700">Revise cada documento separadamente primeiro</p>
                                    </div>
                                  </div>
                                </div>
                                <div className="space-y-3">
                                  <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-bold">4</div>
                                    <div>
                                      <h4 className="font-medium text-green-800">Comparação</h4>
                                      <p className="text-sm text-green-700">Compare documentos similares para identificar padrões</p>
                                    </div>
                                  </div>
                                  <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-bold">5</div>
                                    <div>
                                      <h4 className="font-medium text-green-800">Ação</h4>
                                      <p className="text-sm text-green-700">Implemente recomendações da IA</p>
                                    </div>
                                  </div>
                                  <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-bold">6</div>
                                    <div>
                                      <h4 className="font-medium text-green-800">Documentação</h4>
                                      <p className="text-sm text-green-700">Registre decisões e exportar resultados</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {activeModule === 'timetracking' && (
                        <div className="space-y-6">
                          {/* Guia para Administradores */}
                          <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                            <div className="flex items-center gap-2 mb-4">
                              <Shield className="h-5 w-5 text-orange-600" />
                              <h3 className="font-semibold text-orange-800">Para Administradores / RH</h3>
                            </div>
                            
                            <div className="space-y-4">
                              <div>
                                <h4 className="font-medium text-orange-800 mb-2">1️⃣ Configuração Inicial</h4>
                                <div className="bg-white border border-orange-100 rounded p-3 text-sm">
                                  <ol className="text-orange-700 space-y-2 list-decimal list-inside">
                                    <li>Acesse o menu <strong>Colaboradores → Admin - Ponto</strong></li>
                                    <li>Vá para a aba <strong>"Configurações"</strong></li>
                                    <li>Configure as <strong>Geofences</strong> (áreas permitidas):
                                      <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                                        <li>Clique em "Nova Geofence"</li>
                                        <li>Defina nome (ex: "Sede Principal")</li>
                                        <li>Use "Capturar Localização Atual" ou insira coordenadas</li>
                                        <li>Defina raio em metros (recomendado: 100-500m)</li>
                                      </ul>
                                    </li>
                                    <li>Configure os <strong>Horários de Trabalho</strong>:
                                      <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                                        <li>Clique em "Novo Horário"</li>
                                        <li>Defina nome (ex: "Comercial", "Administrativo")</li>
                                        <li>Configure entrada e saída (ex: 09:00 - 18:00)</li>
                                        <li>Defina intervalo (ex: 12:00 - 13:00)</li>
                                      </ul>
                                    </li>
                                  </ol>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-medium text-orange-800 mb-2">2️⃣ Monitoramento Diário</h4>
                                <div className="bg-white border border-orange-100 rounded p-3 text-sm">
                                  <ol className="text-orange-700 space-y-2 list-decimal list-inside">
                                    <li>Acesse a aba <strong>"Dashboard"</strong> para visão geral</li>
                                    <li>Monitore as <strong>estatísticas em tempo real</strong>:
                                      <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                                        <li>Total de funcionários ativos</li>
                                        <li>Ajustes pendentes de aprovação</li>
                                        <li>Registros recentes do dia</li>
                                      </ul>
                                    </li>
                                    <li>Verifique <strong>notificações automáticas</strong> no header</li>
                                    <li>Acesse a aba <strong>"Ajustes"</strong> para aprovações pendentes</li>
                                  </ol>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-medium text-orange-800 mb-2">3️⃣ Aprovação de Ajustes</h4>
                                <div className="bg-white border border-orange-100 rounded p-3 text-sm">
                                  <ol className="text-orange-700 space-y-2 list-decimal list-inside">
                                    <li>Na aba <strong>"Ajustes"</strong>, visualize solicitações pendentes</li>
                                    <li>Analise cada solicitação:
                                      <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                                        <li>Funcionário solicitante</li>
                                        <li>Tipo de ajuste (correção, adição, remoção)</li>
                                        <li>Justificativa apresentada</li>
                                        <li>Registro original (se existir)</li>
                                      </ul>
                                    </li>
                                    <li>Aprovar ou rejeitar com comentários</li>
                                    <li>Ajustes aprovados geram novo registro com flag "AJUSTADA"</li>
                                  </ol>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-medium text-orange-800 mb-2">4️⃣ Relatórios Administrativos</h4>
                                <div className="bg-white border border-orange-100 rounded p-3 text-sm">
                                  <ol className="text-orange-700 space-y-2 list-decimal list-inside">
                                    <li>Acesse a aba <strong>"Funcionários"</strong></li>
                                    <li>Use os filtros de <strong>período</strong> para análise</li>
                                    <li>Gere relatórios disponíveis:
                                      <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                                        <li><strong>Resumo Geral</strong>: Visão consolidada</li>
                                        <li><strong>Frequência</strong>: Taxa de presença</li>
                                        <li><strong>Horas Extras</strong>: Cálculo detalhado</li>
                                        <li><strong>Banco de Horas</strong>: Saldos individuais</li>
                                      </ul>
                                    </li>
                                    <li>Exporte em formato JSON para análises avançadas</li>
                                  </ol>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-medium text-orange-800 mb-2">5️⃣ Manutenção e Limpeza</h4>
                                <div className="bg-white border border-orange-100 rounded p-3 text-sm">
                                  <ol className="text-orange-700 space-y-2 list-decimal list-inside">
                                    <li>Para <strong>registros órfãos</strong>, use API admin:
                                      <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                                        <li><code className="bg-gray-100 px-1 rounded">POST /api/timetracking/limpar-orfaos/&#123;funcionario_id&#125;</code></li>
                                        <li>Remove registros incompletos maiores que 10 minutos</li>
                                      </ul>
                                    </li>
                                    <li>Para <strong>resetar dia completo</strong> (emergência):
                                      <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                                        <li><code className="bg-gray-100 px-1 rounded">DELETE /api/timetracking/resetar-dia/&#123;funcionario_id&#125;</code></li>
                                        <li>Remove TODOS os registros do dia</li>
                                      </ul>
                                    </li>
                                    <li>Monitore logs do sistema regularmente</li>
                                    <li>Verifique integridade dos backups semanalmente</li>
                                  </ol>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Guia para Funcionários */}
                          <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                            <div className="flex items-center gap-2 mb-4">
                              <Users className="h-5 w-5 text-blue-600" />
                              <h3 className="font-semibold text-blue-800">Para Funcionários</h3>
                            </div>

                            <div className="space-y-4">
                              <div>
                                <h4 className="font-medium text-blue-800 mb-2">1️⃣ Primeiro Acesso</h4>
                                <div className="bg-white border border-blue-100 rounded p-3 text-sm">
                                  <ol className="text-blue-700 space-y-2 list-decimal list-inside">
                                    <li>Faça login com suas credenciais do sistema</li>
                                    <li>Acesse o menu <strong>Colaboradores → Controle de Ponto</strong></li>
                                    <li><strong>Permita localização</strong> quando solicitado pelo navegador</li>
                                    <li>Verifique se o "Status da Localização" está <span className="text-green-600 font-medium">✓ Permitido</span></li>
                                    <li>Para uso mobile, acesse <strong>/ponto/mobile</strong> e "instalar app"</li>
                                  </ol>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-medium text-blue-800 mb-2">2️⃣ Registrando Ponto</h4>
                                <div className="bg-white border border-blue-100 rounded p-3 text-sm">
                                  <ol className="text-blue-700 space-y-2 list-decimal list-inside">
                                    <li>Na aba <strong>"Marcar Ponto"</strong>, siga a sequência CLT:
                                      <div className="ml-4 mt-2 p-2 bg-blue-25 border border-blue-200 rounded">
                                        <div className="text-xs font-mono">
                                          <span className="text-green-600">ENTRADA</span> → 
                                          <span className="text-yellow-600"> SAÍDA INTERVALO</span> → 
                                          <span className="text-blue-600"> RETORNO INTERVALO</span> → 
                                          <span className="text-red-600"> SAÍDA</span>
                                        </div>
                                      </div>
                                    </li>
                                    <li>Clique no botão correspondente ao tipo de registro</li>
                                    <li>Confirme que está <strong>dentro da área permitida</strong></li>
                                    <li>Aguarde a confirmação: <span className="text-green-600">"✓ Registro realizado com sucesso"</span></li>
                                    <li>O registro aparecerá automaticamente em "Registros Recentes"</li>
                                  </ol>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-medium text-blue-800 mb-2">3️⃣ Consultando Histórico</h4>
                                <div className="bg-white border border-blue-100 rounded p-3 text-sm">
                                  <ol className="text-blue-700 space-y-2 list-decimal list-inside">
                                    <li>Na aba <strong>"Histórico"</strong>, visualize todos seus registros</li>
                                    <li>Use filtros por <strong>data de início</strong> e <strong>data fim</strong></li>
                                    <li>Observe os badges de status:
                                      <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                                        <li><Badge variant="default" className="text-xs">VÁLIDA</Badge> - Registro normal</li>
                                        <li><Badge variant="secondary" className="text-xs">AJUSTADA</Badge> - Registro corrigido</li>
                                        <li><Badge variant="destructive" className="text-xs">PENDENTE</Badge> - Ajuste em análise</li>
                                      </ul>
                                    </li>
                                    <li>Verifique <strong>banco de horas</strong> no período</li>
                                  </ol>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-medium text-blue-800 mb-2">4️⃣ Solicitando Ajustes</h4>
                                <div className="bg-white border border-blue-100 rounded p-3 text-sm">
                                  <p className="text-blue-700 mb-2">
                                    <strong>Nota:</strong> Solicitações de ajuste devem ser feitas através da API ou interface administrativa. 
                                    Entre em contato com o RH para correções necessárias.
                                  </p>
                                  <ol className="text-blue-700 space-y-2 list-decimal list-inside">
                                    <li>Identifique o registro que precisa de correção</li>
                                    <li>Anote a <strong>data, horário e tipo</strong> do registro</li>
                                    <li>Prepare a <strong>justificativa detalhada</strong></li>
                                    <li>Contacte o RH para solicitar o ajuste</li>
                                    <li>Acompanhe o status através das notificações</li>
                                  </ol>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-medium text-blue-800 mb-2">5️⃣ Gerando Relatórios</h4>
                                <div className="bg-white border border-blue-100 rounded p-3 text-sm">
                                  <ol className="text-blue-700 space-y-2 list-decimal list-inside">
                                    <li>Na aba <strong>"Relatórios"</strong>, escolha o tipo:
                                      <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                                        <li><strong>Espelho de Ponto</strong>: Resumo mensal completo</li>
                                        <li><strong>AEJ</strong>: Arquivo Eletrônico de Jornada (compliance)</li>
                                      </ul>
                                    </li>
                                    <li>Selecione o <strong>mês e ano</strong> desejado</li>
                                    <li>Clique em <strong>"Gerar Relatório"</strong></li>
                                    <li>Use <strong>"Download"</strong> para salvar em PDF</li>
                                    <li>Relatórios incluem hash SHA-256 para verificação</li>
                                  </ol>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Conteúdo original para outros módulos... */}
                      {activeModule !== 'timetracking' && (
                        <div className="space-y-4">
                          {/* Conteúdo dos outros módulos permanece igual */}
                          <p className="text-gray-600">Selecione um módulo específico para ver as instruções detalhadas.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Tab Dicas - CONTEÚDO ESPECÍFICO PARA REP-P */}
                <TabsContent value="tips" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Lightbulb className="h-5 w-5 text-yellow-600" />
                        Dicas e Melhores Práticas
                      </CardTitle>
                      <CardDescription>
                        {activeModule === 'timetracking' 
                          ? 'Dicas para uso eficiente do Sistema REP-P' 
                          : 'Dicas para maximizar o uso do módulo'
                        }
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {activeModule === 'timetracking' && (
                        <div className="space-y-4">
                          {/* Dicas para Funcionários */}
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <h4 className="font-medium text-green-800 mb-2">💡 Dicas para Funcionários</h4>
                            <ul className="text-green-700 text-sm space-y-1">
                              <li>• <strong>GPS sempre ativo:</strong> Mantenha a localização ativada para registros sem erros</li>
                              <li>• <strong>Sequência correta:</strong> Siga sempre ENTRADA → SAÍDA INTERVALO → RETORNO → SAÍDA</li>
                              <li>• <strong>Dentro da área:</strong> Registre apenas quando estiver na geofence autorizada</li>
                              <li>• <strong>Internet estável:</strong> Conexão ruim pode causar falha no registro</li>
                              <li>• <strong>Horário comercial:</strong> Registros fora do horário (07:00-20:00) geram avisos</li>
                              <li>• <strong>Use PWA mobile:</strong> Para uso frequente, instale como app (/ponto/mobile)</li>
                              <li>• <strong>Verifique confirmação:</strong> Sempre aguarde a mensagem de sucesso</li>
                              <li>• <strong>QR Code alternativo:</strong> Em caso de GPS instável, use autenticação por QR</li>
                            </ul>
                          </div>

                          {/* Dicas para Administradores */}
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="font-medium text-blue-800 mb-2">🛠️ Dicas para Administradores</h4>
                            <ul className="text-blue-700 text-sm space-y-1">
                              <li>• <strong>Geofence adequada:</strong> Configure raio de 100-500m para evitar falsos bloqueios</li>
                              <li>• <strong>Múltiplas geofences:</strong> Crie áreas para diferentes locais de trabalho</li>
                              <li>• <strong>Monitoramento diário:</strong> Verifique dashboard e notificações regularmente</li>
                              <li>• <strong>Aprovação rápida:</strong> Ajustes pendentes devem ser analisados em até 48h</li>
                              <li>• <strong>Backup semanal:</strong> Exporte relatórios mensalmente para arquivo</li>
                              <li>• <strong>Limpeza automática:</strong> Sistema remove órfãos após 10min automaticamente</li>
                              <li>• <strong>Relatórios mensais:</strong> Gere AEJ no final de cada mês para compliance</li>
                              <li>• <strong>Treinamento:</strong> Capacite funcionários na primeira semana de uso</li>
                            </ul>
                          </div>

                          {/* Solução de Problemas */}
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <h4 className="font-medium text-yellow-800 mb-2">🔧 Solução de Problemas Comuns</h4>
                            <div className="space-y-3">
                              <div>
                                <h5 className="font-medium text-yellow-800 text-sm">❌ "Localização fora da área permitida"</h5>
                                <ul className="text-yellow-700 text-xs ml-4 space-y-1">
                                  <li>• Verifique se está dentro da geofence configurada</li>
                                  <li>• Aguarde GPS obter localização precisa (pode levar 30s)</li>
                                  <li>• Saia e entre na área novamente</li>
                                  <li>• Contate admin para ajustar raio da geofence</li>
                                </ul>
                              </div>
                              <div>
                                <h5 className="font-medium text-yellow-800 text-sm">❌ "Já existe uma entrada sem saída"</h5>
                                <ul className="text-yellow-700 text-xs ml-4 space-y-1">
                                  <li>• Há registro órfão de dia anterior</li>
                                  <li>• Admin deve usar: <code className="bg-gray-100 px-1 rounded">POST /api/timetracking/limpar-orfaos</code></li>
                                  <li>• Ou resetar dia completo se necessário</li>
                                </ul>
                              </div>
                              <div>
                                <h5 className="font-medium text-yellow-800 text-sm">❌ Botões não funcionam</h5>
                                <ul className="text-yellow-700 text-xs ml-4 space-y-1">
                                  <li>• Recarregue a página (F5)</li>
                                  <li>• Limpe cache do navegador</li>
                                  <li>• Verifique se JavaScript está habilitado</li>
                                  <li>• Teste em navegador diferente</li>
                                </ul>
                              </div>
                              <div>
                                <h5 className="font-medium text-yellow-800 text-sm">❌ Sistema lento ou travando</h5>
                                <ul className="text-yellow-700 text-xs ml-4 space-y-1">
                                  <li>• Verifique conexão com internet</li>
                                  <li>• Use Chrome ou Firefox atualizado</li>
                                  <li>• Feche outras abas desnecessárias</li>
                                  <li>• Em mobile, use PWA ao invés do navegador</li>
                                </ul>
                              </div>
                            </div>
                          </div>

                          {/* Melhores Práticas de Compliance */}
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                            <h4 className="font-medium text-purple-800 mb-2">⚖️ Melhores Práticas de Compliance</h4>
                            <ul className="text-purple-700 text-sm space-y-1">
                              <li>• <strong>Auditoria mensal:</strong> Revise AEJ de todos funcionários mensalmente</li>
                              <li>• <strong>Backup regulares:</strong> Exporte dados em múltiplos formatos</li>
                              <li>• <strong>Documentação:</strong> Mantenha log de todas configurações e mudanças</li>
                              <li>• <strong>Treinamento legal:</strong> Capacite RH sobre Portaria 671/2021</li>
                              <li>• <strong>Políticas claras:</strong> Defina regras de uso e consequências</li>
                              <li>• <strong>Monitoramento ativo:</strong> Use relatórios para identificar padrões</li>
                              <li>• <strong>Integridade garantida:</strong> Verifique hashes SHA-256 periodicamente</li>
                              <li>• <strong>Armazenamento seguro:</strong> Mantenha backups por no mínimo 5 anos</li>
                            </ul>
                          </div>

                          {/* Recursos Avançados */}
                          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                            <h4 className="font-medium text-indigo-800 mb-2">🚀 Recursos Avançados</h4>
                            <ul className="text-indigo-700 text-sm space-y-1">
                              <li>• <strong>API REST completa:</strong> 15+ endpoints para integrações</li>
                              <li>• <strong>Notificações automáticas:</strong> Sistema de alertas em tempo real</li>
                              <li>• <strong>Múltiplas interfaces:</strong> Web, Admin e PWA para diferentes necessidades</li>
                              <li>• <strong>Proteção temporal:</strong> Registros recentes protegidos contra limpeza</li>
                              <li>• <strong>Validações CLT:</strong> Sequência e intervalos conforme legislação</li>
                              <li>• <strong>Geofencing inteligente:</strong> Múltiplas áreas com validação precisa</li>
                              <li>• <strong>Banco de horas:</strong> Cálculo automático de saldos</li>
                              <li>• <strong>Ajustes controlados:</strong> Workflow de aprovação para correções</li>
                            </ul>
                          </div>
                        </div>
                      )}

                      {activeModule === 'contract-intelligence' && (
                        <div className="space-y-4">
                          {/* Dicas para Otimização da Análise */}
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <h4 className="font-medium text-green-800 mb-3">💡 Dicas para Otimizar a Análise</h4>
                            <ul className="text-green-700 text-sm space-y-2">
                              <li>• <strong>Qualidade do documento:</strong> PDFs nativos (não escaneados) têm maior precisão de análise</li>
                              <li>• <strong>Resolução de imagens:</strong> Para JPG/PNG, use no mínimo 300 DPI para melhor OCR</li>
                              <li>• <strong>Formato preferido:</strong> DOC/DOCX preservam formatação e estrutura original</li>
                              <li>• <strong>Tamanho ideal:</strong> Documentos até 5MB processam mais rapidamente</li>
                              <li>• <strong>Múltiplos arquivos:</strong> Faça upload em lotes para análise comparativa mais eficaz</li>
                              <li>• <strong>Nomeação consistente:</strong> Use nomes descritivos para facilitar organização</li>
                              <li>• <strong>Forçar nova análise:</strong> Use o botão 🧠 se suspeitar de análise incompleta</li>
                              <li>• <strong>Aguarde processamento:</strong> IA precisa de 30-120s dependendo da complexidade</li>
                            </ul>
                          </div>

                          {/* Preparação de Documentos */}
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="font-medium text-blue-800 mb-3">📋 Preparação de Documentos</h4>
                            <ul className="text-blue-700 text-sm space-y-2">
                              <li>• <strong>Remova anexos desnecessários:</strong> Foque no contrato principal para análise precisa</li>
                              <li>• <strong>Páginas relevantes:</strong> Para contratos longos, inclua apenas cláusulas essenciais</li>
                              <li>• <strong>Documente versões:</strong> Use sufixos como "_v1", "_final", "_assinado" nos nomes</li>
                              <li>• <strong>Organize por categoria:</strong> Agrupe contratos similares para comparação</li>
                              <li>• <strong>Verifique legibilidade:</strong> Textos borrados ou cortados prejudicam a análise</li>
                              <li>• <strong>Idioma português:</strong> IA otimizada para contratos em português brasileiro</li>
                              <li>• <strong>Estrutura clara:</strong> Contratos bem formatados geram melhores resultados</li>
                              <li>• <strong>Backup original:</strong> Mantenha cópias dos documentos originais</li>
                            </ul>
                          </div>

                          {/* Interpretação de Resultados */}
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <h4 className="font-medium text-yellow-800 mb-3">🔍 Como Interpretar Resultados</h4>
                            <div className="space-y-3">
                              <div>
                                <h5 className="font-medium text-yellow-800 text-sm mb-1">📊 Nível de Confiança</h5>
                                <ul className="text-yellow-700 text-xs ml-4 space-y-1">
                                  <li>• <strong>90-100%:</strong> Análise altamente confiável, dados extraídos precisos</li>
                                  <li>• <strong>70-89%:</strong> Boa qualidade, revise dados críticos manualmente</li>
                                  <li>• <strong>50-69%:</strong> Qualidade moderada, validação manual recomendada</li>
                                  <li>• <strong>Abaixo 50%:</strong> Documento pode ter problemas de legibilidade</li>
                                </ul>
                              </div>
                              <div>
                                <h5 className="font-medium text-yellow-800 text-sm mb-1">⚠️ Classificação de Riscos</h5>
                                <ul className="text-yellow-700 text-xs ml-4 space-y-1">
                                  <li>• <strong>Alto:</strong> Cláusulas críticas que requerem atenção imediata</li>
                                  <li>• <strong>Médio:</strong> Pontos importantes para revisão e negociação</li>
                                  <li>• <strong>Baixo:</strong> Aspectos menores que podem ser observados</li>
                                  <li>• <strong>Zero riscos:</strong> Pode indicar documento muito simples ou análise incompleta</li>
                                </ul>
                              </div>
                              <div>
                                <h5 className="font-medium text-yellow-800 text-sm mb-1">📈 Entidades Extraídas</h5>
                                <ul className="text-yellow-700 text-xs ml-4 space-y-1">
                                  <li>• <strong>Muitas entidades:</strong> Contrato detalhado, boa extração de dados</li>
                                  <li>• <strong>Poucas entidades:</strong> Documento simples ou OCR com dificuldades</li>
                                  <li>• <strong>Dados críticos:</strong> Priorize datas, valores e partes contratuais</li>
                                </ul>
                              </div>
                            </div>
                          </div>

                          {/* Boas Práticas de Workflow */}
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                            <h4 className="font-medium text-purple-800 mb-3">🔄 Workflow Recomendado</h4>
                            <div className="space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">1</div>
                                    <h5 className="font-medium text-purple-800 text-sm">Triagem Inicial</h5>
                                  </div>
                                  <p className="text-purple-700 text-xs ml-8">Analise contratos individuais primeiro para entender padrões</p>
                                </div>
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">2</div>
                                    <h5 className="font-medium text-purple-800 text-sm">Análise Comparativa</h5>
                                  </div>
                                  <p className="text-purple-700 text-xs ml-8">Compare documentos similares para identificar inconsistências</p>
                                </div>
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">3</div>
                                    <h5 className="font-medium text-purple-800 text-sm">Priorização</h5>
                                  </div>
                                  <p className="text-purple-700 text-xs ml-8">Foque primeiro em contratos com riscos "Alto" identificados</p>
                                </div>
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">4</div>
                                    <h5 className="font-medium text-purple-800 text-sm">Documentação</h5>
                                  </div>
                                  <p className="text-purple-700 text-xs ml-8">Exporte resultados e mantenha histórico das análises</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Solução de Problemas */}
                          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <h4 className="font-medium text-red-800 mb-3">🔧 Solução de Problemas</h4>
                            <div className="space-y-3">
                              <div>
                                <h5 className="font-medium text-red-800 text-sm">❌ "Erro no upload"</h5>
                                <ul className="text-red-700 text-xs ml-4 space-y-1">
                                  <li>• Verifique se o arquivo tem menos de 10MB</li>
                                  <li>• Confirme formato suportado: PDF, DOC, DOCX, JPG, PNG</li>
                                  <li>• Tente converter o arquivo ou reduza a resolução</li>
                                  <li>• Recarregue a página e tente novamente</li>
                                </ul>
                              </div>
                              <div>
                                <h5 className="font-medium text-red-800 text-sm">❌ "Análise com baixa confiança"</h5>
                                <ul className="text-red-700 text-xs ml-4 space-y-1">
                                  <li>• Documento pode estar mal escaneado ou com baixa qualidade</li>
                                  <li>• Converta para PDF nativo se for imagem</li>
                                  <li>• Melhore a resolução ou contraste do arquivo</li>
                                  <li>• Remova páginas com ruído visual</li>
                                </ul>
                              </div>
                              <div>
                                <h5 className="font-medium text-red-800 text-sm">❌ "Poucos dados extraídos"</h5>
                                <ul className="text-red-700 text-xs ml-4 space-y-1">
                                  <li>• Documento pode ser muito simples ou padronizado</li>
                                  <li>• Verifique se contém cláusulas contratuais típicas</li>
                                  <li>• Forçe nova análise com botão 🧠</li>
                                  <li>• Compare com documento similar para validar</li>
                                </ul>
                              </div>
                              <div>
                                <h5 className="font-medium text-red-800 text-sm">❌ "Sistema lento"</h5>
                                <ul className="text-red-700 text-xs ml-4 space-y-1">
                                  <li>• IA pode estar processando múltiplos documentos</li>
                                  <li>• Aguarde alguns minutos antes de tentar novamente</li>
                                  <li>• Faça upload de poucos documentos por vez</li>
                                  <li>• Limpe cache do navegador se problema persistir</li>
                                </ul>
                              </div>
                            </div>
                          </div>

                          {/* Recursos Avançados */}
                          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                            <h4 className="font-medium text-indigo-800 mb-3">🚀 Recursos Avançados</h4>
                            <ul className="text-indigo-700 text-sm space-y-2">
                              <li>• <strong>Integração com Emergent LLM:</strong> Análise usando modelos de IA de última geração</li>
                              <li>• <strong>API REST disponível:</strong> Integre com sistemas externos via endpoints dedicados</li>
                              <li>• <strong>Armazenamento seguro:</strong> Todos os documentos ficam protegidos no sistema</li>
                              <li>• <strong>Histórico completo:</strong> Mantenha registro de todas as análises realizadas</li>
                              <li>• <strong>Exportação em JSON:</strong> Dados estruturados para análises externas</li>
                              <li>• <strong>Comparação simultânea:</strong> Analise até 10 documentos em paralelo</li>
                              <li>• <strong>OCR multilíngue:</strong> Suporte a contratos em diversos idiomas</li>
                              <li>• <strong>Análise semântica:</strong> Compreende contexto e significado além do texto literal</li>
                            </ul>
                          </div>

                          {/* Conformidade e Segurança */}
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <h4 className="font-medium text-gray-800 mb-3">🔒 Conformidade e Segurança</h4>
                            <ul className="text-gray-700 text-sm space-y-2">
                              <li>• <strong>LGPD Compliance:</strong> Sistema atende requisitos da Lei Geral de Proteção de Dados</li>
                              <li>• <strong>Dados criptografados:</strong> Informações protegidas em trânsito e armazenamento</li>
                              <li>• <strong>Controle de acesso:</strong> Apenas usuários autorizados podem acessar documentos</li>
                              <li>• <strong>Logs de auditoria:</strong> Todas as ações ficam registradas para compliance</li>
                              <li>• <strong>Backup automático:</strong> Documentos e análises são preservados automaticamente</li>
                              <li>• <strong>Retenção configurável:</strong> Defina políticas de retenção de dados</li>
                              <li>• <strong>Exclusão segura:</strong> Remoção definitiva de dados quando necessário</li>
                              <li>• <strong>Certificação ISO:</strong> Infraestrutura segue padrões internacionais</li>
                            </ul>
                          </div>
                        </div>
                      )}

                      {/* Conteúdo original para outros módulos permanece igual */}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TutorialPage;